import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Contact() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation();
  const { ref: infoRef, isVisible: infoVisible } = useScrollAnimation();

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={headerRef}
          className={`text-center mb-12 ${headerVisible ? 'animate-fade-in-down' : 'opacity-0'}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 rounded-full mb-4">
            <i className="ri-mail-line text-teal-600"></i>
            <span className="text-sm font-medium text-teal-700">Get in Touch</span>
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-4">Let's Connect</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Interested in healthcare analytics, collaboration, or hospital onboarding? We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Right side - Contact form */}
          <div
            ref={formRef}
            className={`bg-gradient-to-br from-teal-50 to-blue-50 rounded-3xl p-8 shadow-xl ${formVisible ? 'animate-fade-in-left' : 'opacity-0'}`}
          >
            <form className="space-y-6" data-readdy-form id="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-user-line text-gray-400"></i>
                  </div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                    placeholder="Enter your full name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-mail-line text-gray-400"></i>
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-bookmark-line text-gray-400"></i>
                  </div>
                  <select
                    id="subject"
                    name="subject"
                    required
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base appearance-none bg-white cursor-pointer"
                  >
                    <option value="">Select a topic</option>
                    <option value="hospital-partnership">Hospital Partnership</option>
                    <option value="collaboration">Collaboration Opportunity</option>
                    <option value="data-inquiry">Data & Analytics Inquiry</option>
                    <option value="general">General Question</option>
                    <option value="support">Technical Support</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base resize-none"
                  placeholder="Tell us more about your inquiry..."
                ></textarea>
                <div className="text-right text-xs text-gray-500 mt-1">
                  <span id="char-count">0</span>/500
                </div>
              </div>

              <button
                type="submit"
                className="w-full px-8 py-4 bg-gradient-to-r from-teal-600 to-blue-600 text-white rounded-xl font-semibold text-lg hover:from-teal-700 hover:to-blue-700 transition-all hover:shadow-xl hover:-translate-y-1 whitespace-nowrap"
              >
                <i className="ri-send-plane-fill mr-2"></i>
                Send Message
              </button>

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to our{' '}
                <a href="/privacy" className="text-teal-600 hover:text-teal-700 underline">
                  Privacy Policy
                </a>
              </p>
            </form>
          </div>

          {/* Left side - Image and info */}
          <div
            ref={infoRef}
            className={`space-y-8 ${infoVisible ? 'animate-fade-in-right animation-delay-200' : 'opacity-0'}`}
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-80 w-full">
              <img
                src="https://public.readdy.ai/ai/img_res/b735cba1d845c764e33ecbb66fc817c2.jpg"
                alt="Mother and child"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-900/40 to-transparent"></div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl mb-4">
                  <i className="ri-hospital-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Hospital Partners</h3>
                <p className="text-sm text-gray-600">Join our network of trusted healthcare providers</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-xl mb-4">
                  <i className="ri-team-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Collaboration</h3>
                <p className="text-sm text-gray-600">Work together on healthcare analytics projects</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-xl mb-4">
                  <i className="ri-bar-chart-box-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Data Insights</h3>
                <p className="text-sm text-gray-600">Access healthcare analytics and research</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-xl mb-4">
                  <i className="ri-question-answer-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Support</h3>
                <p className="text-sm text-gray-600">Get help with platform features and services</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <script dangerouslySetInnerHTML={{
        __html: `
          document.addEventListener('DOMContentLoaded', function() {
            const textarea = document.getElementById('message');
            const charCount = document.getElementById('char-count');
            
            if (textarea && charCount) {
              textarea.addEventListener('input', function() {
                charCount.textContent = this.value.length;
              });
            }
          });
        `
      }} />
    </section>
  );
}
